import * as React from 'react'
import { ChevronDown, ChevronUp, RotateCcw, X } from 'lucide-react'

import { useDataTable } from '@/components/data-table/data-table-provider'
import { Button } from '@/components/ui/button'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/custom/table'
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import { useClaimDetailQuery } from '@/features/claims/hooks/useClaimDetailQuery'
import type { ClaimDetail, ClaimHeader } from '@/features/claims/types'
import { useSheetRowNavigation } from '@/hooks/useSheetRowNavigation'
import { ApiError } from '@/lib/api'
import {
  claimClientLabel,
  formatClaimCurrency,
  formatClaimDate,
} from './claimsTableSchema'

const show = (value: string | null) => value || '-'

function MetaRow({ label, value }: { label: string; value: React.ReactNode }) {
  const empty =
    value === null ||
    value === undefined ||
    (typeof value === 'string' && value.trim().length === 0)
  return (
    <div className="flex justify-between gap-4 py-1.5 text-sm">
      <span className="text-muted-foreground shrink-0">{label}</span>
      <span className="min-w-0 text-right break-words">
        {empty ? '-' : value}
      </span>
    </div>
  )
}

function ClaimLines({ lines }: { lines: ClaimDetail['lines'] }) {
  return (
    <div
      role="region"
      tabIndex={0}
      className="w-full overflow-x-auto rounded-md border"
      data-testid="claim-lines-scroll-region"
      aria-label="Claim lines table scroll region">
      <Table className="min-w-[900px]">
        <TableHeader>
          <TableRow>
            <TableHead scope="col">Line ID</TableHead>
            <TableHead scope="col">Service Date</TableHead>
            <TableHead scope="col">Status</TableHead>
            <TableHead scope="col">Revenue Code</TableHead>
            <TableHead scope="col">Service Code</TableHead>
            <TableHead scope="col" className="text-right">
              Units
            </TableHead>
            <TableHead scope="col" className="text-right">
              Billed
            </TableHead>
            <TableHead scope="col" className="text-right">
              Paid
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {lines.map((line) => (
            <TableRow key={line.id}>
              <TableCell className="whitespace-nowrap">
                {line.source_line_id}
              </TableCell>
              <TableCell className="whitespace-nowrap">
                {formatClaimDate(line.service_date) ?? '-'}
              </TableCell>
              <TableCell>{show(line.status)}</TableCell>
              <TableCell>{show(line.revenue_code)}</TableCell>
              <TableCell>{show(line.service_code)}</TableCell>
              <TableCell className="text-right whitespace-nowrap tabular-nums">
                {show(line.units)}
              </TableCell>
              <TableCell className="text-right whitespace-nowrap tabular-nums">
                {formatClaimCurrency(line.claim_amount) ?? '-'}
              </TableCell>
              <TableCell className="text-right whitespace-nowrap tabular-nums">
                {formatClaimCurrency(line.paid_amount) ?? '-'}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  )
}

export function ClaimsSheetDetails({
  onClose,
  onUnavailable,
}: {
  onClose?: () => void
  onUnavailable?: () => void
} = {}) {
  const { table, rowSelection, isLoading } = useDataTable<
    ClaimHeader,
    unknown
  >()
  const navigation = useSheetRowNavigation(
    table,
    rowSelection,
    Boolean(isLoading)
  )
  const claim = navigation.selectedRow?.original
  const detailQuery = useClaimDetailQuery(claim?.id)

  React.useEffect(() => {
    if (
      detailQuery.error instanceof ApiError &&
      (detailQuery.error.status === 403 || detailQuery.error.status === 404)
    ) {
      onUnavailable?.()
      table.resetRowSelection()
    }
  }, [detailQuery.error, onUnavailable, table])

  const handleOpenChange = React.useCallback(
    (open: boolean) => {
      if (!open) onClose?.()
      navigation.onOpenChange(open)
    },
    [navigation, onClose]
  )

  return (
    <Sheet
      open={Boolean(navigation.selectedRowKey)}
      onOpenChange={handleOpenChange}>
      <SheetContent
        onCloseAutoFocus={(event) => event.preventDefault()}
        onOpenAutoFocus={(event) => event.preventDefault()}
        className="bg-neutral-ui-bg w-full max-w-none gap-0 overflow-y-auto rounded-l-xl p-0 sm:max-w-3xl"
        showCloseButton={false}>
        <SheetHeader className="bg-neutral-ui-bg sticky top-0 z-10">
          <div className="flex items-start justify-between gap-2">
            <SheetTitle className="min-w-0 flex-1 truncate text-left text-lg font-semibold">
              {claim ? `Claim ${claim.source_claim_id}` : 'Claim Details'}
            </SheetTitle>
            <Button
              size="icon"
              variant="ghost"
              className="h-7 w-7"
              disabled={!navigation.prevId}
              onClick={navigation.onPrev}
              aria-label="Previous claim"
              title="Previous claim (↑)">
              <ChevronUp className="h-5 w-5" />
            </Button>
            <Button
              size="icon"
              variant="ghost"
              className="h-7 w-7"
              disabled={!navigation.nextId}
              onClick={navigation.onNext}
              aria-label="Next claim"
              title="Next claim (↓)">
              <ChevronDown className="h-5 w-5" />
            </Button>
            <SheetClose asChild>
              <Button size="icon" variant="ghost" className="h-7 w-7">
                <X className="h-5 w-5" />
                <span className="sr-only">Close</span>
              </Button>
            </SheetClose>
          </div>
          {claim ? (
            <div className="text-neutral-low-contrast text-left text-sm">
              {claimClientLabel(claim) ?? 'Client unavailable'}
            </div>
          ) : null}
        </SheetHeader>

        <SheetDescription className="sr-only">
          Selected claim details and claim lines
        </SheetDescription>

        <div className="space-y-5 px-4 pb-4">
          {claim ? (
            <section aria-labelledby="claim-summary-heading">
              <h3
                id="claim-summary-heading"
                className="mb-2 text-sm font-semibold">
                Claim Summary
              </h3>
              <div className="divide-y rounded-md border px-3">
                <MetaRow
                  label="Service Date"
                  value={formatClaimDate(claim.service_date)}
                />
                <MetaRow label="Status" value={claim.status} />
                <MetaRow
                  label="Billed"
                  value={formatClaimCurrency(claim.claim_amount)}
                />
                <MetaRow
                  label="Paid"
                  value={formatClaimCurrency(claim.paid_amount)}
                />
                <MetaRow label="Provider Name" value={claim.provider_name} />
                <MetaRow label="Provider NPI" value={claim.provider_npi} />
                <MetaRow
                  label="Source Patient ID"
                  value={claim.source_patient_id}
                />
              </div>
            </section>
          ) : null}

          <section aria-labelledby="claim-lines-heading">
            <h3 id="claim-lines-heading" className="mb-2 text-sm font-semibold">
              Claim Lines
            </h3>
            {!claim || detailQuery.isLoading ? (
              <p className="text-muted-foreground py-8 text-center text-sm">
                Loading claim lines…
              </p>
            ) : detailQuery.isError ? (
              <div className="flex flex-col items-center gap-3 py-8 text-center">
                <p className="text-muted-foreground text-sm">
                  Claim lines could not be loaded.
                </p>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => void detailQuery.refetch()}>
                  <RotateCcw className="mr-2 h-4 w-4" />
                  Retry claim lines
                </Button>
              </div>
            ) : detailQuery.data?.lines.length === 0 ? (
              <p className="text-muted-foreground py-8 text-center text-sm">
                No claim lines.
              </p>
            ) : detailQuery.data ? (
              <ClaimLines lines={detailQuery.data.lines} />
            ) : null}
          </section>
        </div>
      </SheetContent>
    </Sheet>
  )
}
